/**
 * What screen a board opens on, and which of the waiting stage's four steps it
 * is standing on. Every answer comes from the server's own board payload, so
 * closing the tab and coming back lands on exactly the step the draft is really
 * at: nothing here counts, guesses or times anything.
 */
import { PROCESSING_VIDEO_STATES, VIDEO_READY } from "../../shared/config";
import type { BoardStateResponse } from "../../shared/types";

/** The six steps between a recording and a board. */
type BoardStep = "upload" | "playable" | "listen" | "names" | "build" | "chapters" | "design";

/**
 * Where a draft is: the entry screen, one of the steps, or the board itself. A
 * live has its own step before the design, `write`: its page written from notes.
 */
export type BoardPhase = "entry" | BoardStep | "write" | "board";

interface StepCopy {
  key: BoardStep;
  /** The word in the row of four, on the stage's rail. */
  label: string;
}

/** The steps, in order. What each one SAYS lives in `stage/copy.ts`. */
export const BOARD_STEPS: readonly StepCopy[] = [
  { key: "upload", label: "Upload" },
  { key: "playable", label: "Make it playable" },
  { key: "listen", label: "Listen to the talk" },
  { key: "names", label: "Check the names" },
  { key: "build", label: "Build the board" },
  { key: "chapters", label: "Check the chapters" },
  { key: "design", label: "Design the page" },
];

/** A live's four steps, from picking a time to its two pages. */
type LiveStep = "when" | "about" | "write" | "design";

export const LIVE_STEPS: ReadonlyArray<{ key: LiveStep; label: string }> = [
  { key: "when", label: "When" },
  { key: "about", label: "What it's about" },
  { key: "write", label: "Write the page" },
  { key: "design", label: "Design the page" },
];

export function stepIndex(step: BoardStep): number {
  return BOARD_STEPS.findIndex((s) => s.key === step);
}

/**
 * The recording's own words are being written down. It is not `ready` yet, but
 * the pieces are behind it: the wait from here is the listening, so that is the
 * step the host is shown.
 */
const VIDEO_TRANSCRIBING = "transcribing";

/**
 * The screen this board resumes on.
 *
 * Read it top down: a live page is edited on the board like any other, because
 * the board is the only place a page, its moments and its follow-ups are
 * edited; a board that has been read is the board; a transcript with no read is
 * the last step; a recording that is still encoding is the second; anything
 * else needs a recording first.
 */
export function resumePhase(board: BoardStateResponse): BoardPhase {
  if (board.status === "published") return "board";
  // A live has no recording and no checks: its page is written from the notes,
  // then designed, then it is the board.
  if (board.scheduleMode === "live") {
    if (board.readAt === null) return "write";
    return board.pagePairs > 0 && board.pageHtml === null ? "design" : "board";
  }
  // A read board with pages written but none chosen is standing on the fifth
  // step; a read board from the static page flow (no pages) is the board.
  if (board.readAt !== null) {
    // The chapters check: a gate between the read landing its cards and the
    // pages being designed from them. Only the server's own "not yet" holds it.
    if (board.chaptersChecked === false && board.pagePairs === 0 && board.pageHtml === null) return "chapters";
    return board.pagePairs > 0 && board.pageHtml === null ? "design" : "board";
  }
  // The names check: a gate between the transcript landing and the read firing.
  // One glance (or an auto-pass when the talk repeats no names) opens it for good.
  if (board.transcriptPresent && !board.namesChecked) return "names";
  if (board.transcriptPresent) return "build";
  const state: string | null = board.video.state;
  if (state === VIDEO_READY || state === VIDEO_TRANSCRIBING) return "listen";
  if (state && (PROCESSING_VIDEO_STATES.includes(state) || state === "error")) return "playable";
  return "entry";
}

/** Is this page already live? Then the board edits it rather than publishing it. */
export function isLive(board: BoardStateResponse | null): boolean {
  return board?.status === "published";
}

/** Is the recording's encode the thing that failed? Then waiting is pointless. */
export function videoFailed(board: BoardStateResponse | null): boolean {
  return board?.video.state === "error";
}

/** Should the board keep asking the server what this draft has become? */
export function keepPolling(phase: BoardPhase | "loading", board: BoardStateResponse | null): boolean {
  if (videoFailed(board)) return false;
  return phase === "playable" || phase === "listen" || phase === "build" || phase === "write";
}

/** Bytes as the entry screen says them. */
export function sizeLabel(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  const gb = bytes / 1024 ** 3;
  if (gb >= 1) return `${gb.toFixed(1)} GB`;
  return `${Math.max(1, Math.round(bytes / 1024 ** 2))} MB`;
}
